import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { IGeoApiCommune } from '../models/geo-api-commune.model';

/**
 * Utilisation de l'api geo du gouvernement Français.
 * https://geo.api.gouv.fr/decoupage-administratif/communes
 */
@Injectable({ providedIn: 'root' })
export class BaryInseeService {
  private GEO_API = 'https://geo.api.gouv.fr/';
  private CARTO_IGN_API = 'https://apicarto.ign.fr/';

  private FIELDS_PARAM = 'fields';
  private LAT_PARAM = 'lat';
  private CP_PARAM = 'codePostal';
  private LON_PARAM = 'lon';

  private SELECTED_FIELDS = 'code,nom';
  private readonly DEFAULT_API_TIMEOUT = 2000;

  public resourceUrl = this.GEO_API + 'communes';

  constructor(protected http: HttpClient) {}

  public getInseeCityCodeByGeoCoord(lat: string, lon: string, cp: string, city?: string): Observable<IGeoApiCommune | undefined> {
    if (lat && lon) {
      const url = new URL(this.resourceUrl);
      url.searchParams.append(this.LAT_PARAM, lat);
      url.searchParams.append(this.LON_PARAM, lon);
      url.searchParams.append(this.FIELDS_PARAM, this.SELECTED_FIELDS);

      return this.callGeoApi(url, cp, city);
    } else {
      return of(undefined);
    }
  }

  public getInseeByCP(cp: string | undefined, city: string | undefined, insee: string | null | undefined): Observable<IGeoApiCommune | undefined> {
    if (insee) {
      return of({ code: insee });
    }
    if (!cp) {
      return of(undefined);
    }
    const url = new URL(this.resourceUrl);
    url.searchParams.append(this.CP_PARAM, cp);
    url.searchParams.append(this.FIELDS_PARAM, this.SELECTED_FIELDS);

    return this.callGeoApi(url, cp, city);
  }

  private callCartoIGNApi(cp: string, nomCommune?: string): Observable<IGeoApiCommune | undefined> {
    return this.http
      .get<
        HttpResponse<
          {
            codePostal: string;
            codeCommune: string;
            nomCommune: string;
            libelleAcheminement: string;
          }[]
        >
      >(this.CARTO_IGN_API + 'api/codes-postaux/communes/' + cp)
      .pipe(
        timeout(this.DEFAULT_API_TIMEOUT),
        map(result => {
          // retourne le premier object qui matche
          if (result.status === 200 && Array.isArray(result.body)) {
            if (result.body.length === 1) {
              return result.body[0];
            } else {
              return result.body.find(elt => elt['nomCommune'].toLowerCase() === nomCommune?.toLowerCase());
            }
          }
          return undefined;
        }),
        map(it => it),
        map(it =>
          // map to IGeoApiCommune
          ({ nom: it!.nomCommune, code: it!.codeCommune })
        )
      );
  }

  private callGeoApi(url: URL, cp: string, city?: string): Observable<IGeoApiCommune | undefined> {
    return this.http.get<IGeoApiCommune[]>(url.toString()).pipe(
      timeout(this.DEFAULT_API_TIMEOUT),
      map(communes => {
        if (communes.length === 1) {
          return communes[0];
        } else if (communes.length === 0) {
          console.warn('No code insee for ' + url.toString() + '.');
          return undefined;
        } else {
          console.warn('Multiple Insee code for ' + url.toString() + '. We take the first !');
          return communes[0];
        }
      }),
      catchError(err => {
        console.error(err);
        if (err === 'TimeoutError') {
          return this.callCartoIGNApi(cp, city);
        } else {
          return of(undefined);
        }
      })
    );
  }
}
